<template>
  <Teleport to="body">
    <div class="edit-modal-overlay" v-bind="$attrs" @click.self.stop="$emit('close')">
      <div class="edit-modal" :class="{ 'narrow-modal': editingSession || uniquePreviousSessions.length === 0 }">
      <div class="edit-modal-header">
        <div class="modal-intro">
          <span class="material-symbols-outlined">edit_calendar</span>
          <div class="modal-headline">
            <h1>{{ editingSession ? 'Edit Custom Session' : 'Add Custom Session' }}</h1>
          </div>
        </div>
        <div class="modal-controls">
          <span class="material-symbols-outlined close-icon" @click.stop="$emit('close')">close</span>
        </div>
      </div>
      <div class="edit-modal-body">
        <div class="form-section">
          <form @submit.prevent="save">
            <div class="form-group">
              <label for="sessionName">Session Name</label>
              <input 
                type="text" 
                id="sessionName" 
                v-model="localSession.name" 
                required 
                placeholder="e.g., Study Group, Meeting">
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label for="sessionDay">Day</label>
                <select id="sessionDay" v-model="localSession.day" required>
                  <option value="">Select day</option>
                  <option v-for="day in days" :key="day" :value="day">{{ fullDayName(day) }}</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="sessionStartTime">Start Time</label>
                <input 
                  type="time" 
                  id="sessionStartTime" 
                  v-model="localSession.startTime" 
                  min="08:00" 
                  max="20:00" 
                  required>
              </div>
              
              <div class="form-group">
                <label for="sessionEndTime">End Time</label>
                <input 
                  type="time" 
                  id="sessionEndTime" 
                  v-model="localSession.endTime" 
                  min="08:00" 
                  max="20:00" 
                  required>
              </div>
            </div>
            
            <div class="form-group">
              <label for="sessionLocation">Location (optional)</label>
              <input 
                type="text" 
                id="sessionLocation" 
                v-model="localSession.location" 
                placeholder="e.g., Library, Room 101">
            </div>
            
            <div class="form-group checkbox-wrapper">
              <label class="switch">
                <input type="checkbox" v-model="allowOverlap">
                <span class="slider round"></span>
              </label>
              <span class="checkbox-label">Allow Overlap</span>
            </div>

            <div class="form-actions">
              <button 
                v-if="editingSession"
                type="button"
                class="delete-btn"
                @click.stop="$emit('delete', editingSession)"
                title="Delete session">
                Delete
              </button>
              <div class="form-actions-right">
                <button type="button" class="cancel-btn" @click.stop="$emit('close')">Cancel</button>
                <button type="submit" class="save-btn">
                  {{ editingSession ? 'Update' : 'Add' }} Session
                </button>
              </div>
            </div>
          </form>
        </div>
        
        <div class="sidebar-section" v-show="!editingSession && uniquePreviousSessions.length > 0">
          <h4>Other Custom Sessions</h4>
          <div class="session-list">
             <div 
               v-for="s in uniquePreviousSessions" 
               :key="s.id" 
               class="session-item"
               @click.stop="loadSession(s)"
             >
                <div class="session-item-name">{{ s.name }}</div>
                <div class="session-item-details">{{ fullDayName(s.day) }} • {{ s.startTime }}-{{ s.endTime }}</div>
             </div>
             <div v-if="uniquePreviousSessions.length === 0" class="empty-state">
               No existing sessions.
             </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: 'EditSessionModal',
  inheritAttrs: false,
  props: {
    editingSession: {
      type: Object,
      default: null
    },
    days: {
      type: Array,
      required: true
    },
    initialData: {
      type: Object,
      default: () => ({
        name: '',
        day: '',
        startTime: '',
        endTime: '',
        location: ''
      })
    }
  },
  emits: ['close', 'save', 'delete', 'edit-existing'],
  data() {
    return {
      localSession: { ...this.initialData },
      allowOverlap: false
    };
  },
  computed: {
    allCustomSessions() {
      return this.$store.state.preferences.customSessions || [];
    },
    uniquePreviousSessions() {

        return [...this.allCustomSessions].sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  watch: {
    initialData: {
      handler(newVal) {
        this.localSession = { ...newVal };
        this.allowOverlap = newVal.allowOverlap || false;
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    save() {
      if (this.localSession.startTime >= this.localSession.endTime) {
        alert('End time must be after start time');
        return;
      }
      this.$emit('save', { ...this.localSession, allowOverlap: this.allowOverlap });
    },
    loadSession(session) {
      this.$emit('edit-existing', session);
    },
    fullDayName(day) {
      if (!day) return '';
      const map = {
        'mon': 'Monday',
        'tue': 'Tuesday',
        'wed': 'Wednesday',
        'thu': 'Thursday',
        'fri': 'Friday',
        'sat': 'Saturday',
        'sun': 'Sunday'
      };
      return map[day.toLowerCase()] || day;
    }
  }
};
</script>

<style scoped>

.edit-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.edit-modal {
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  transition: max-width 0.3s ease;
}

.edit-modal.narrow-modal {
  max-width: 480px;
}

.edit-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  z-index: 2;
}

.modal-intro {
  display: flex;
  align-items: center;
  gap: 15px;
}

.modal-intro .material-symbols-outlined {
  font-size: 30px;
  color: #9ABCE4;
}

.modal-headline {
  display: flex;
  flex-direction: column;
}

.edit-modal-header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.modal-controls {
  display: flex;
  align-items: center;
}

.edit-modal-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.form-section {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.sidebar-section {
  width: 220px;
  background-color: #f9fafb;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  padding: 16px;
  overflow-y: auto;
}

.sidebar-section h4 {
  margin-top: 0;
  margin-bottom: 12px;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.session-list {
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  max-height: 300px;
  gap: 8px;
}

.session-item {
  background: white;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s;
}

.session-item:hover {
  border-color: #5e94d4;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  transform: translateY(-1px);
}

.session-item-name {
  font-weight: 600;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 2px;
}

.session-item-details {
  font-size: 11px;
  color: #6b7280;
}

.empty-state {
  color: #9ca3af;
  font-size: 13px;
  text-align: center;
  margin-top: 20px;
  font-style: italic;
}

.close-icon {
  font-size: 20px;
  cursor: pointer;
  color: #9ca3af;
  transition: color 0.2s;
  user-select: none;
}

.close-icon:hover {
  color: #4b5563;
}

.form-group {
  margin-bottom: 10px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 13px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  height: 30px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  color: #111827;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #5e94d4;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f3f4f6;
}

.form-actions-right {
  display: flex;
  gap: 10px;
  margin-left: auto; 
}

.delete-btn + .form-actions-right {
    margin-left: 0; 
}

.save-btn,
.cancel-btn,
.delete-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn {
  background: #9ABCE4;
  color: white;
  border: 1px solid #9ABCE4;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.save-btn:hover {
  background: #5E94D4;
  border-color: #5E94D4;
  transform: translateY(-1px);
}

.cancel-btn {
  background: white;
  color: #9ABCE4;
  border: 1px solid #9ABCE4;
}

.cancel-btn:hover {
  color: #5E94D4;
  border-color: #5E94D4;
  background: #fff;
}

.delete-btn {
  background: #ffffff;
  color: #991b1b;
  border: 1px solid #991b1b;
}

.delete-btn:hover {
  background: #fee2e2;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 15px;

}

.switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e5e7eb;
  transition: .3s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

input:checked + .slider {
  background-color: #5E94D4;
}

input:checked + .slider:before {
  transform: translateX(16px);
}

.checkbox-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 5px;
}

.material-symbols-outlined {
  font-size: 18px;
}
</style>
